/**
 * US capital gains for individuals (Form 1040 Schedule D), estates/trusts (Form 1041 Schedule D) and C corporations
 * (Form 1120 Schedule D). Specific identification per lot (default FIFO, via the parcel strategies); holding period > 1
 * year = long-term. Not tax advice.
 * Sources: Schedule D (Form 1041) 2025 https://www.irs.gov/pub/irs-pdf/f1041sd.pdf (Part IV $3,000 limit; Part V
 * 0 %/15 % breakpoints $3,250/$15,900); Rev. Proc. 2025-32 (2026: $3,300/$16,250); Form 1041-ES 2024 ($3,150/$15,450);
 * Schedule D (Form 1120) 2025 https://www.irs.gov/pub/irs-pdf/f1120sd.pdf ; IRC §11(b) 21 % corporate rate;
 * IRC §1211(a) (corporate losses only against gains) and §1212(a) (3-year carry-back, 5-year carry-forward as short-term).
 */
import { Decimal, dec } from '../money.js';
import { addDays, addYears, diffDays, financialYearOf } from '../dates.js';
import { KeelError, assertNever } from '../errors.js';
/** Individuals (incl. households, sole proprietors, partners), estates/trusts (Form 1041), C corporations (incl. clubs). */
export const usTaxpayer = (entityType) => {
    switch (entityType) {
        case 'individual':
        case 'household':
        case 'sole_trader':
        case 'partnership': return 'individual';
        case 'trust': return 'trust';
        case 'company':
        case 'club': return 'corporation';
        case 'smsf': throw new KeelError('UNSUPPORTED', 'US capital gains are not modelled for an Australian SMSF');
        default: return assertNever(entityType, 'entity type');
    }
};
export const US_CORPORATE_RATE = 0.21;
/** Estates & trusts: maximum taxable income taxed at 0 % and at 15 % on long-term gains (cents), by tax year. */
export const US_TRUST_LT_BREAKPOINTS = {
    '2024': [315_000, 1_545_000], '2025': [325_000, 1_590_000], '2026': [330_000, 1_625_000],
};
export const usTrustBreakpoints = (year) => {
    const keys = Object.keys(US_TRUST_LT_BREAKPOINTS).sort();
    const key = US_TRUST_LT_BREAKPOINTS[year] ? year : year > (keys[keys.length - 1] ?? '') ? keys[keys.length - 1] : keys[0];
    return US_TRUST_LT_BREAKPOINTS[key ?? ''] ?? [0, 0];
};
/**
 * Tax on long-term gains for an estate/trust, stacked on top of `ordinaryCents` of taxable ordinary income (0/15/20 %).
 * ponytail: assumes no other trust income and no distributions to beneficiaries (DNI); short-term gains are the ordinary stack.
 */
export const usTrustLongTermTax = (longTermCents, ordinaryCents, year) => {
    const [zero, fifteen] = usTrustBreakpoints(year);
    const start = Math.max(0, ordinaryCents), end = start + Math.max(0, longTermCents);
    const band = (lo, hi) => Math.max(0, Math.min(end, hi) - Math.max(start, lo));
    return roundHE(dec(band(zero, fifteen)).mul(0.15).plus(dec(band(fifteen, Number.MAX_SAFE_INTEGER)).mul(0.2)));
};
/** Net capital losses deductible against ordinary income per year (married filing separately: $1,500 — not modelled). */
export const US_CAPITAL_LOSS_LIMIT_CENTS = 300_000;
/** Long-term = held more than one year (the anniversary day is still short-term). */
export const isLongTermUS = (acquiredDate, disposalDate) => disposalDate > addYears(acquiredDate, 1);
/** ponytail: long-term rate band picked from the ordinary marginal rate (≤ 12 % → 0 %, ≤ 35 % → 15 %, else 20 %); NIIT and state tax ignored. */
export const usLongTermRate = (marginalRate) => (marginalRate <= 0.12 ? 0 : marginalRate <= 0.35 ? 0.15 : 0.2);
function roundHE(d) { return new Decimal(d).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber(); }
const computeDisposal = (slice, proceedsCents, date) => {
    const { parcel } = slice;
    const gain = proceedsCents - slice.costBaseCents;
    return {
        id: `cgt:${slice.tradeId ?? slice.corporateActionId ?? 'adhoc'}:${parcel.id}`,
        entityId: parcel.entityId, accountId: parcel.accountId, securityId: parcel.securityId, parcelId: parcel.id,
        tradeId: slice.tradeId, corporateActionId: slice.corporateActionId, date, acquiredDate: parcel.acquiredDate, units: slice.units,
        proceedsCents, costBaseCents: slice.costBaseCents, reducedCostBaseCents: slice.costBaseCents,
        grossGainCents: gain, isLoss: gain < 0,
        discountEligible: isLongTermUS(parcel.acquiredDate, date), // US: "eligible" = long-term (preferential rates)
        holdingDays: diffDays(parcel.acquiredDate, date), method: 'other', indexedCostBaseCents: null,
        financialYear: financialYearOf(date, 1, 1).label, createdAt: slice.timestamp, updatedAt: slice.timestamp,
    };
};
const isUsSummary = (s) => s.jurisdiction === 'US' && 'taxableShortTermCents' in s;
/**
 * Calendar-year summary: net short-term and long-term separately (carryovers keep their character; a plain numeric
 * carry-forward is treated as short-term), then net across. Individuals and estates/trusts deduct up to $3,000 of a net loss
 * (short-term first). Corporations deduct nothing: a net capital loss carries back 3 years (exposed, not applied) and
 * forward 5 years as a short-term loss.
 */
export const summariseUsYear = (events, carriedForwardInCents, ctx, opts = {}) => {
    const taxpayer = usTaxpayer(ctx.entityType);
    const corp = taxpayer === 'corporation';
    const evs = opts.fy ? events.filter((e) => e.financialYear === opts.fy) : [...events];
    let stG = 0, stL = 0, ltG = 0, ltL = 0;
    for (const e of evs) {
        const lt = isLongTermUS(e.acquiredDate, e.date);
        if (e.grossGainCents >= 0) {
            if (lt)
                ltG += e.grossGainCents;
            else
                stG += e.grossGainCents;
        }
        else if (lt)
            ltL -= e.grossGainCents;
        else
            stL -= e.grossGainCents;
    }
    const cf = opts.carriedForwardByKind ?? { shortTermCents: Math.max(0, carriedForwardInCents), longTermCents: 0 };
    const netST = stG - stL - cf.shortTermCents;
    const netLT = ltG - ltL - cf.longTermCents;
    let taxST = 0, taxLT = 0, lossST = 0, lossLT = 0;
    if (netST >= 0 && netLT >= 0) {
        taxST = netST;
        taxLT = netLT;
    }
    else if (netST < 0 && netLT >= 0) {
        if (netLT + netST >= 0)
            taxLT = netLT + netST;
        else
            lossST = -(netLT + netST);
    }
    else if (netST >= 0 && netLT < 0) {
        if (netST + netLT >= 0)
            taxST = netST + netLT;
        else
            lossLT = -(netST + netLT);
    }
    else {
        lossST = -netST;
        lossLT = -netLT;
    }
    const deduction = corp ? 0 : Math.min(US_CAPITAL_LOSS_LIMIT_CENTS, lossST + lossLT);
    const dST = Math.min(lossST, deduction);
    const carry = corp
        ? { shortTermCents: lossST + lossLT, longTermCents: 0 } // corporate carryovers are short-term
        : { shortTermCents: lossST - dST, longTermCents: lossLT - (deduction - dST) };
    const cfOut = carry.shortTermCents + carry.longTermCents;
    const cfInTotal = cf.shortTermCents + cf.longTermCents;
    const net = taxST + taxLT;
    const fy = opts.fy ?? evs[0]?.financialYear ?? '';
    const yearLoss = Math.max(0, stL + ltL - stG - ltG); // this year's own net capital loss (carry-back excludes prior carryovers)
    const y = Number(fy);
    return {
        fy, taxpayer,
        carryBack: corp && yearLoss > 0 && Number.isFinite(y) ? { amountCents: Math.min(yearLoss, cfOut), years: [y - 3, y - 2, y - 1].map(String) } : null,
        carryforwardExpiresAfter: corp && cfOut > 0 && Number.isFinite(y) ? String(y + 5) : null,
        grossGainsCents: stG + ltG, trustCapitalGainsCents: 0, discountableGainsCents: ltG, nonDiscountGainsCents: stG,
        currentYearLossesCents: stL + ltL, lossesAppliedCents: Math.min(stL + ltL, stG + ltG), carriedForwardInCents: cfInTotal,
        carriedForwardAppliedCents: Math.min(cfInTotal, Math.max(0, stG + ltG - stL - ltL)),
        discountCents: 0, netCapitalGainCents: net, carriedForwardOutCents: cfOut, postRegimeGainCents: 0, regime2027: null,
        atoLabels: { '18H': stG + ltG, '18A': net, '18V': cfOut },
        events: evs, jurisdiction: 'US', taxableShortTermCents: taxST, taxableLongTermCents: taxLT, carryforwardOut: carry,
        labels: { shortTermGain: stG, longTermGain: ltG, netShortTerm: netST, netLongTerm: netLT, lossDeductionUsed: deduction, carriedForwardOut: cfOut },
    };
};
export const usJurisdiction = {
    code: 'US',
    discountRate: () => 0,
    isDiscountEligible: isLongTermUS,
    computeDisposal: (slice, proceeds, date) => computeDisposal(slice, proceeds, date),
    summariseYear: summariseUsYear,
    estimateTax: (s, ctx) => {
        const st = isUsSummary(s) ? s.taxableShortTermCents : Math.max(0, s.netCapitalGainCents);
        const lt = isUsSummary(s) ? s.taxableLongTermCents : 0;
        const kind = usTaxpayer(ctx.entityType);
        switch (kind) {
            case 'corporation': return roundHE(dec(st + lt).mul(US_CORPORATE_RATE));
            // ponytail: trust short-term gains at ctx.marginalRate rather than the compressed ordinary brackets
            case 'trust': return roundHE(dec(st).mul(ctx.marginalRate)) + usTrustLongTermTax(lt, st, s.fy);
            case 'individual': return roundHE(dec(st).mul(ctx.marginalRate).plus(dec(lt).mul(usLongTermRate(ctx.marginalRate))));
            default: return assertNever(kind, 'taxpayer');
        }
    },
    marginalGainRate: (ctx, longTerm) => {
        const kind = usTaxpayer(ctx.entityType);
        if (kind === 'corporation')
            return US_CORPORATE_RATE;
        if (kind === 'trust')
            return longTerm ? 0.2 : ctx.marginalRate; // trust brackets compress: most gains reach 20 %
        return longTerm ? usLongTermRate(ctx.marginalRate) : ctx.marginalRate;
    },
};
/**
 * Flags loss disposals with substantially identical purchases (same security, same entity, any account) within
 * ±`windowDays` of the sale. Shares sold in that same sale are never replacements; each purchased unit is used once,
 * earliest loss first. Basis is NOT adjusted — callers decide; the adjustment amount is exposed.
 */
export const washSaleWarnings = (cgtEvents, trades, opts = {}) => {
    const window = opts.windowDays ?? 30;
    const used = new Map();
    const soldParcels = (e) => new Set(cgtEvents.filter((x) => x.tradeId !== null && x.tradeId === e.tradeId).map((x) => x.parcelId).concat(e.parcelId));
    const losses = cgtEvents.filter((e) => e.isLoss).sort((a, b) => (a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? -1 : 1));
    const out = [];
    for (const e of losses) {
        const excluded = soldParcels(e);
        const from = addDays(e.date, -window), to = addDays(e.date, window);
        const buys = trades
            .filter((t) => t.side === 'buy' && t.entityId === e.entityId && t.securityId === e.securityId && t.date >= from && t.date <= to && !excluded.has(`parcel:${t.id}`))
            .sort((a, b) => (a.date === b.date ? (a.id < b.id ? -1 : 1) : a.date < b.date ? -1 : 1));
        let need = dec(e.units);
        const ids = [];
        let matched = dec(0);
        for (const t of buys) {
            if (need.lte(0))
                break;
            const avail = dec(t.units).minus(used.get(t.id) ?? 0);
            if (avail.lte(0))
                continue;
            const take = Decimal.min(avail, need);
            used.set(t.id, (used.get(t.id) ?? dec(0)).plus(take));
            need = need.minus(take);
            matched = matched.plus(take);
            ids.push(t.id);
        }
        if (matched.isZero())
            continue;
        const loss = -e.grossGainCents;
        const disallowed = roundHE(dec(loss).mul(matched).div(e.units));
        out.push({
            cgtEventId: e.id, tradeId: e.tradeId, securityId: e.securityId, date: e.date, lossCents: loss, soldUnits: e.units,
            replacementTradeIds: ids, replacementUnits: matched.toFixed(), disallowedLossCents: disallowed, basisAdjustmentCents: disallowed,
            message: `Possible wash sale: ${matched.toFixed()} units repurchased within ${window} days; ${disallowed} of the ${loss} loss may be disallowed`,
        });
    }
    return out;
};
//# sourceMappingURL=us.js.map